import {
  Area,
  CartesianGrid,
  AreaChart as RechartsArea,
  XAxis,
  YAxis,
} from 'recharts';
import { ComponentProps, ReactNode } from 'react';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/shadcn/chart';
import { formatDateAxis, groupByMonth } from '@/utils/explorer';
import { IAreaGraph } from '@/types/explorer';
import useBreakpoint from '@/hooks/useBreakpoints';
import ActiveDot from './ActiveDot';

interface Props {
  id?: string;
  data: IAreaGraph[];
  showYAxis?: boolean;
  valueLabel?: string;
  yAxisProps?: Partial<ComponentProps<typeof YAxis>>;
  xAxisProps?: Partial<ComponentProps<typeof XAxis>>;
  fillColor?: string;
  margin?: ComponentProps<typeof RechartsArea>['margin'];
  customTooltip?: (props: any) => ReactNode;
}

const resetMonths = (data: IAreaGraph[]) => groupByMonth(data.map(d => d.date));

const getLargestValue = (data: IAreaGraph[]) => {
  return data.reduce((acc, point) => {
    if (point.value === undefined || point.value === null) {
      return acc;
    }
    return Math.max(acc, point.value);
  }, 0);
};

const AreaChart: React.FC<Props> = ({
  id,
  data,
  valueLabel = 'Value',
  showYAxis,
  margin,
  yAxisProps,
  xAxisProps,
  fillColor = 'hsl(var(--azure-400))',
}) => {
  let months = resetMonths(data);
  let displayedIdxs: number[] = [];
  const largestValue = getLargestValue(data);
  const largestValueLength = Math.round(largestValue).toLocaleString('en-US', {
    notation: 'standard',
    roundingMode: 'trunc',
    maximumFractionDigits: 0,
  }).length;

  const { isMobile } = useBreakpoint();

  const chartConfig = {
    value: {
      label: valueLabel,
      color: fillColor,
    },
  } satisfies ChartConfig;

  // Responsive XAxis styles
  const getXAxisStyle = () => {
    if (isMobile) {
      return {
        fontSize: '10px',
        textTransform: 'capitalize' as const,
        fill: 'hsl(var(--neutral-0))',
      };
    }
    return {
      fontSize: '12px',
      textTransform: 'capitalize' as const,
      fill: 'hsl(var(--neutral-0))',
    };
  };

  const leftMargin = largestValueLength * 2 - 40;

  return (
    <ChartContainer config={chartConfig} className="size-full">
      <RechartsArea
        data={data}
        margin={{
          top: 10,
          right: 0,
          left: Math.max(leftMargin, -25),
          bottom: 10,
          ...margin,
        }}>
        <defs>
          <linearGradient id={`${id}Fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={fillColor} stopOpacity={0.8} />
            <stop offset="90%" stopColor={fillColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} horizontal opacity={0.5} />
        <XAxis
          axisLine={false}
          angle={-40}
          padding={{ left: 0, right: 10 }}
          tickMargin={15}
          dataKey="date"
          dx={-10}
          interval={0}
          tickLine={false}
          type="category"
          style={getXAxisStyle()}
          allowDuplicatedCategory={false}
          tickFormatter={(d: any, idx: number) => {
            // Recharts has this "double" re-render on the tickFormatter
            // which causes a problem to display the grouped months on the initial render
            // so we need to reset the "grouped month displayed" on the 2nd re-render
            if (displayedIdxs.includes(idx)) {
              months = resetMonths(data);
              displayedIdxs = [idx];
            } else {
              displayedIdxs.push(idx);
            }
            return formatDateAxis(d, months, true);
          }}
          {...xAxisProps}
        />
        {showYAxis ? (
          <YAxis
            style={{
              fontSize: '12px',
              textTransform: 'uppercase',
              fill: 'hsl(var(--neutral-0))',
            }}
            scale="linear"
            interval={0}
            allowDuplicatedCategory={false}
            axisLine={false}
            tickLine={false}
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(value: number) => {
              return value.toLocaleString('en-US', {
                notation: 'compact',
                roundingMode: 'floor',
                maximumFractionDigits: 1,
              });
            }}
            {...yAxisProps}
          />
        ) : null}
        <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
        <Area
          type="basis"
          dataKey="value"
          stroke={fillColor}
          fill={`url(#${id}Fill)`}
          activeDot={<ActiveDot />}
        />
      </RechartsArea>
    </ChartContainer>
  );
};

export default AreaChart;
